// Barcode field UI: symbology, data (+ live validation / check digit), bar height, module width in
// whole printer dots, human-readable text, quiet zone.
import { h } from '../dom.js';
import * as B from '../../itf/barcode.js';
import * as schema from '../../itf/schema.js';
import { localBox } from '../geometry.js';
import { section, row, full, mmInput, textInput, selectInput, segmented, message } from './controls.js';

const DPI = B.PRINTER_DPI || 305;
const unitsPerDot = () => (typeof B.unitsPerDot === 'function' ? B.unitsPerDot(DPI) : 2540 / DPI);
const snapDots = (u) => (typeof B.snapNarrowBarWidth === 'function' ? B.snapNarrowBarWidth(u, DPI) : Math.round(Math.max(1, Math.round(u / unitsPerDot())) * unitsPerDot()));
const dotsOf = (u) => Math.max(1, Math.round((Number(u) || 0) / unitsPerDot()));
const spec = (f) => (f && typeof B.symbologyById === 'function' ? B.symbologyById(f.symbology) : null);

const HR_OPTIONS = [
  { value: 'DISPLAY_WITH_CHECKDIGIT', label: 'Show with check digit' },
  { value: 'DISPLAY_DATA', label: 'Show data' },
  { value: 'DO_NOT_DISPLAY', label: 'Hide' },
];

function dotStepper(b, { ariaLabel, get, set }) {
  const minus = h('button', { class: 'tog', type: 'button', title: 'One printer dot thinner', 'aria-label': `${ariaLabel}: one dot less`, style: { flex: '0 0 30px' } }, '−');
  const plus = h('button', { class: 'tog', type: 'button', title: 'One printer dot wider', 'aria-label': `${ariaLabel}: one dot more`, style: { flex: '0 0 30px' } }, '+');
  const input = mmInput(b, { ariaLabel: `${ariaLabel} (mm)`, get, set, min: 1, max: 500, step: Math.round(unitsPerDot()) / 100, snap: snapDots, decimals: 2 });
  minus.addEventListener('click', () => input.report(set(Math.round(Math.max(1, dotsOf(get()) - 1) * unitsPerDot()))));
  plus.addEventListener('click', () => input.report(set(Math.round((dotsOf(get()) + 1) * unitsPerDot()))));
  const dots = h('span', { class: 'hint', style: { whiteSpace: 'nowrap' } });
  b.add(() => {
    const v = get();
    dots.textContent = v ? `${dotsOf(v)} dot${dotsOf(v) === 1 ? '' : 's'}` : '';
    minus.disabled = !v || dotsOf(v) <= 1;
  });
  return h('div', { style: { display: 'flex', gap: '4px', alignItems: 'flex-start' } }, h('div', { style: { flex: '1', minWidth: '0' } }, input), minus, plus, h('div', { style: { paddingTop: '6px' } }, dots));
}

export function barcodeSection(b, ctx) {
  const f = () => ctx.cur();
  const oneD = () => (spec(f()) ? spec(f()).oneD !== false : !['DataMatrix', 'QR'].includes(f()?.symbology));

  const verify = () => {
    const cur = f();
    if (!cur || typeof B.validateData !== 'function') return null;
    return B.validateData(cur.symbology, cur.data, { autoCheckDigit: cur.autoCheckDigit ?? undefined });
  };

  const unverified =
    schema.BARCODE_TEMPLATE_VERIFIED === false
      ? h('span', { class: 'status-unverified', title: 'Barcode format not yet verified on printer — the barcode XML this editor writes has not been test-printed on the SmartDate X65' }, 'not yet verified on printer')
      : null;

  const rows = [
    row(
      'Type',
      selectInput(b, {
        ariaLabel: 'Barcode type',
        options: () => (B.SYMBOLOGIES || []).map((s) => ({ value: s.id, label: s.label })),
        get: () => f()?.symbology,
        set: (v) => ctx.edit({ symbology: v }),
      }),
    ),
    row(
      'Data',
      textInput(b, {
        ariaLabel: 'Barcode data',
        primary: true,
        get: () => f()?.data,
        set: (v) => ctx.edit({ data: v }, 'data'),
        placeholder: '',
      }),
    ),
    message(b, () => {
      const cur = f();
      if (!cur) return null;
      const res = verify();
      if (!res) return null;
      if (!res.ok) return { kind: 'bad', text: `${res.message || 'Invalid data'}${spec(cur)?.dataHint ? ` — ${spec(cur).dataHint}` : ''}` };
      if (res.checkDigit !== undefined && res.checkDigit !== null) {
        const digits = String(res.normalized ?? cur.data).trim();
        const fullLen = { EAN13: 13, EAN8: 8 }[cur.symbology];
        const printed = fullLen && digits.length === fullLen - 1 ? `${digits}${res.checkDigit}` : digits;
        return { kind: 'good', text: `✓ Check digit ${res.checkDigit} · prints ${printed}` };
      }
      return { kind: 'good', text: `✓ Valid ${spec(cur)?.label || 'barcode'} data` };
    }, { role: 'status' }),
  ];

  // 1-D only
  const oneDRows = h(
    'div',
    { style: { display: 'grid', gap: '8px' } },
    row('Bar height', mmInput(b, { ariaLabel: 'Bar height (mm)', get: () => f()?.barHeight, set: (u) => ctx.edit({ barHeight: u }, 'barHeight'), min: 100, max: 7100, step: 0.1 }), { scrub: { step: 0.05 } }),
    row('Module', dotStepper(b, { ariaLabel: 'Narrow bar (module) width', get: () => f()?.narrowBarWidth, set: (u) => ctx.edit({ narrowBarWidth: u }, 'nbw') })),
    row('Text', segmented(b, { ariaLabel: 'Human-readable text', options: HR_OPTIONS.map((o) => ({ ...o, label: o.label.replace('Show with check digit', 'With check') })), get: () => f()?.textDisplay || 'DISPLAY_DATA', set: (v) => ctx.edit({ textDisplay: v }) })),
  );
  // 2-D only
  const twoDRows = h('div', { style: { display: 'grid', gap: '8px' } }, row('Cell size', dotStepper(b, { ariaLabel: 'Cell size', get: () => f()?.cellWidth, set: (u) => ctx.edit({ cellWidth: u }, 'cell') })));
  b.add(() => {
    if (!f()) return;
    oneDRows.hidden = !oneD();
    twoDRows.hidden = oneD();
  });

  const size = h('div', { class: 'readout' });
  b.add(() => {
    const cur = f();
    if (!cur) return;
    const box = localBox(cur);
    size.textContent = `${(box.w / 100).toFixed(2)} × ${(box.h / 100).toFixed(2)} mm including quiet zone${cur.quietMargin ? '' : ' (off)'}`;
  });

  rows.push(
    oneDRows,
    twoDRows,
    row('Quiet zone', segmented(b, { ariaLabel: 'Quiet zone', options: [{ value: true, label: 'On', title: 'Blank margin around the bars (recommended)' }, { value: false, label: 'Off' }], get: () => !!f()?.quietMargin, set: (v) => ctx.edit({ quietMargin: v }) })),
    full(size),
  );
  return section('Barcode', 'barcode', rows.filter(Boolean), { extra: unverified });
}
